<template>
    <BaseCard padding="sm" class="flex items-center gap-3 min-w-0">
        <div class="grow min-w-0">
            <div class="details mb-1 max-w-48 break-words">{{ fileName }}</div>
            <div class="flex flex-wrap gap-2">
                <BaseChip size="2xs" class="uppercase max-w-full whitespace-normal break-all h-auto min-h-5.5">{{ fileType }}</BaseChip>
                <BaseChip v-if="fileSize" size="2xs">{{ fileSize }}</BaseChip>
            </div>
        </div>
        <BaseButton v-if="isRemovable" theme="neutral" size="sm" icon-only :aria-label="t('components.fileAttachedTag.remove', { fileName })" @click="$emit('remove')">
            <i class="bi bi-x icon-bold leading-none" aria-hidden="true" />
        </BaseButton>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import type { FileUploadArgs } from "~vue/types/chat";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

type FileAttachedTagProps = {
    file: File | FileUploadArgs;
    isRemovable?: boolean;
};

const { file, isRemovable = false } = defineProps<FileAttachedTagProps>();

defineEmits<{
    remove: [];
}>();

const fileType = computed(() => {
    const type = file.type;
    if (!type) {
        return "txt";
    }
    return type;
});

const fileName = computed(() => {
    const MAX_FILE_NAME_LENGTH = 30;
    const name = file.name;
    if (name && name.length >= MAX_FILE_NAME_LENGTH) {
        return `${name.slice(0, MAX_FILE_NAME_LENGTH / 2)}...${name.slice(name.length - MAX_FILE_NAME_LENGTH / 2, name.length)}`;
    }
    return name;
});

const fileSize = computed(() => {
    const size = file.size;
    if (!size) {
        return undefined;
    }
    if (size < 1024) {
        return `${size} B`;
    } else if (size < 1024 * 1024) {
        return `${Math.floor(size / 1024)} KB`;
    } else {
        return `${Math.floor(size / 1024 / 1024)} MB`;
    }
});
</script>
